import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Building2, GraduationCap, PartyPopper, Check, X } from "lucide-react";
import { EVENT_MODULES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Building2, GraduationCap, PartyPopper };
const GOLD_BTN =
  "group relative w-full overflow-hidden rounded-2xl bg-[#0a0a0b] py-4 text-2xl text-[#f8b818] shadow-[0_14px_35px_rgba(36,28,13,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(248,184,24,0.45)] disabled:opacity-60";

export default function Events() {
  const [booking, setBooking] = useState(null);
  const [view, setView] = useState("form"); // form | done
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", guests: "20" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [refCode, setRefCode] = useState("");
  const [pickingDate, setPickingDate] = useState(false);
  const honeypot = useRef(null);
  const openedAt = useRef(0);

  const openBooking = (m) => {
    setBooking(m);
    setView("form");
    setForm({ name: "", email: "", phone: "", date: "", guests: "20" });
    setError("");
    setRefCode("");
    setPickingDate(false);
    openedAt.current = Date.now();
  };

  const close = () => setBooking(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (name.length < 2) {
      setError("Please enter your name");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      setError("Please enter a contact number");
      return;
    }
    if (!form.date) {
      setError("Please pick your preferred date");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/event-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: booking.title,
          name,
          email,
          phone,
          date: form.date,
          guests: Number(form.guests) || 1,
          website: honeypot.current?.value || "",
          elapsed_ms: Date.now() - openedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Something went wrong — please try again");
      setRefCode(data.ref || "");
      setView("done");
    } catch (err) {
      setError(err.message || "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="events" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="events-section">
      <div className="grid gap-5 lg:grid-cols-3">
        {EVENT_MODULES.map((m, i) => {
          const Icon = ICONS[m.icon] || PartyPopper;
          return (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="card-dark group flex flex-col overflow-hidden transition-all hover:-translate-y-1.5 hover:border-[#f8b818]/40"
              data-testid={`event-module-${i}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={m.img}
                  alt={m.alt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141416] via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-full bg-[#f8b818] p-2.5 text-[#0a0a0b] shadow-[0_6px_18px_rgba(36,28,13,0.25)]">
                  <Icon size={18} />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="text-2xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>{m.title}</h3>
                <p className="mt-2 flex-1 text-sm text-[#8a7b5e]">{m.text}</p>
                <ul className="mt-4 space-y-1.5">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm font-bold text-[#241c0d]/85">
                      <Check size={14} className="text-[#f8b818]" /> {b}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openBooking(m)}
                  className="btn-gold mt-6 self-start !px-5 !py-2.5 !text-xs"
                  data-testid={`event-book-btn-${i}`}
                >
                  {{ corporate: "Enquire about your event", school: "Enquire now", birthday: "Plan your celebration" }[m.id] || "Book this event"}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {booking && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={close}
          data-testid="event-booking-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[28px] bg-[#fff7e8] p-6 pt-9 shadow-[0_0_0_3px_#f8b818,0_0_0_9px_rgba(248,184,24,0.16),0_40px_90px_rgba(20,18,12,0.55)] sm:p-8 sm:pt-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="event-booking-card"
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-1.5 rounded-t-[28px] bg-gradient-to-r from-[#f8b818] via-[#f9c92e] to-[#f8b818]" />
            <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#f8b818]/25 blur-2xl" />
            <button
              onClick={close}
              className="absolute right-4 top-4 rounded-full border border-[#241c0d]/15 bg-white/70 p-2 text-[#6b5a3a] transition-colors hover:border-[#c87700] hover:text-[#c87700]"
              aria-label="Close"
              data-testid="event-booking-close-btn"
            >
              <X size={16} />
            </button>

            {view === "form" ? (
              <>
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f8b818] text-[#14120c] shadow-[0_4px_0_#7a5600,0_10px_20px_rgba(248,184,24,0.45)]">
                  <PartyPopper size={26} />
                </span>
                <p className="mt-4 text-3xl leading-none text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>{booking.title}</p>
                <p className="mt-2 text-sm font-bold text-[#5c5240]">Fill in your details — we confirm by email.</p>
                <form onSubmit={submit} className="mt-5 space-y-3" data-testid="event-booking-form">
                  <input
                    className="input-dark w-full"
                    placeholder="Your name (e.g. Thandi Mokoena)"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={80}
                    data-testid="event-booking-name"
                  />
                  <input
                    className="input-dark w-full"
                    type="email"
                    placeholder="Your email — we confirm here"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    maxLength={120}
                    data-testid="event-booking-email"
                  />
                  <input
                    className="input-dark w-full"
                    type="tel"
                    placeholder="Contact number"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    maxLength={25}
                    data-testid="event-booking-phone"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-extrabold uppercase tracking-widest text-[#5c5240]">
                        Preferred date
                      </span>
                      <input
                        className="input-dark w-full"
                        type={pickingDate || form.date ? "date" : "text"}
                        onFocus={() => setPickingDate(true)}
                        placeholder="Pick a date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        min={new Date().toISOString().slice(0, 10)}
                        aria-label="Preferred date"
                        data-testid="event-booking-date"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-extrabold uppercase tracking-widest text-[#5c5240]">
                        Guests
                      </span>
                      <input
                        className="input-dark w-full"
                        type="number"
                        min="1"
                        max="200"
                        placeholder="How many?"
                        value={form.guests}
                        onChange={(e) => setForm({ ...form, guests: e.target.value })}
                        aria-label="Number of guests"
                        data-testid="event-booking-guests"
                      />
                    </label>
                  </div>
                  <input
                    ref={honeypot}
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />
                  {error && (
                    <p className="text-sm font-bold text-[#dc2626]" data-testid="event-booking-error">
                      {error}
                    </p>
                  )}
                  <button type="submit" disabled={busy} className={GOLD_BTN} data-testid="event-booking-submit">
                    <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#f8b818]/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                    <span className="relative" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
                      {busy ? "Sending…" : "Send booking request"}
                    </span>
                  </button>
                  <p className="text-center text-[11px] font-bold text-[#8a7b5e]">
                    No payment now — we confirm availability by email first.
                  </p>
                </form>
              </>
            ) : (
              <>
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f8b818] text-[#14120c] shadow-[0_4px_0_#7a5600,0_10px_20px_rgba(248,184,24,0.45)]">
                  <PartyPopper size={26} />
                </span>
                <p className="mt-4 text-3xl leading-none text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>YOU'RE BOOKED!</p>
                <p className="mt-3 text-base leading-relaxed text-[#5c5240]" data-testid="event-booking-success">
                  Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}! Your {booking.title.toLowerCase()} request is in
                  {refCode ? (
                    <>
                      {" "}
                      — reference <b className="font-extrabold text-[#241c0d]">{refCode}</b>
                    </>
                  ) : null}
                  . We'll confirm availability by email shortly.
                </p>
                <button onClick={close} className={GOLD_BTN + " mt-6"} data-testid="event-booking-done-btn">
                  <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#f8b818]/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                  <span className="relative" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
                    Back to the fun
                  </span>
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}
